import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Certificate } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

const ImageUploader: React.FC<{ value: string; onChange: (value: string) => void; }> = ({ value, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `certificates/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                onChange(downloadURL);
            } catch (error) {
                showToast('Image upload failed.', { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    return (
        <div 
            className="relative w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors bg-gray-50"
            onClick={() => !isUploading && inputRef.current?.click()}
        >
            {isUploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                    <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div>
                </div>
            )}
            {value ? (
                <img src={value} alt="Certificate" className="max-h-full max-w-full object-contain p-2" />
            ) : (
                <div className="text-center text-gray-500">
                    <UploadIcon className="mx-auto h-8 w-8"/>
                    <span className="mt-2 text-sm">Click to upload image</span>
                </div>
            )}
            <input 
                type="file"
                ref={inputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/gif, image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
            />
        </div>
    );
};


const AdminCertificateFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [certificate, setCertificate] = useState<Omit<Certificate, 'id'>>({
        title: '',
        issuingBody: '',
        year: new Date().getFullYear(),
        imageUrl: '',
        order: 0,
    });

    useEffect(() => {
        const fetchCertificate = async () => {
            if (isEditing && id) {
                const docRef = doc(db, 'certificates', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCertificate(docSnap.data() as Certificate);
                } else {
                    showToast('Certificate not found.', { type: 'error' });
                    navigate('/admin/certificates');
                }
            } else {
                const snapshot = await getDocs(collection(db, 'certificates'));
                setCertificate(prev => ({ ...prev, order: snapshot.size }));
            }
            setIsLoading(false);
        };
        fetchCertificate();
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCertificate(prev => ({ ...prev, [name]: name === 'year' ? parseInt(value) : value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (isEditing && id) {
                await updateDoc(doc(db, 'certificates', id), certificate);
                showToast('Certificate updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'certificates'), certificate);
                showToast('Certificate added successfully!', { type: 'success' });
            }
            navigate('/admin/certificates');
        } catch (error) {
            showToast('Failed to save certificate.', { type: 'error' });
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="text-center p-8">Loading...</div>;

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Certificate' : 'Add New Certificate'}</h2>
            <form onSubmit={handleSubmit}>
                <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto space-y-6">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">Certificate Title</label>
                        <input type="text" name="title" id="title" value={certificate.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label htmlFor="issuingBody" className="block text-sm font-medium text-gray-700">Issuing Body</label>
                        <input type="text" name="issuingBody" id="issuingBody" value={certificate.issuingBody} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label htmlFor="year" className="block text-sm font-medium text-gray-700">Year Issued</label>
                        <input type="number" name="year" id="year" value={certificate.year} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Image</label>
                        <ImageUploader value={certificate.imageUrl} onChange={val => setCertificate(p => ({ ...p, imageUrl: val }))} />
                    </div>
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={() => navigate('/admin/certificates')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? 'Saving...' : 'Save Certificate'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdminCertificateFormPage;